import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";
import { useDemo } from "@/demo/DemoContext";

export default function TourPill() {
  const { tourActive, tourStep, totalSteps, currentStep, nextStep, prevStep, stopTour } = useDemo();

  if (!tourActive || !currentStep) return null;

  const isLast = tourStep === totalSteps;
  const progress = (tourStep / totalSteps) * 100;

  return (
    <div
      data-testid="tour-pill"
      className="enter-up"
      style={{
        position: "fixed",
        bottom: 76,
        right: 20,
        zIndex: 60,
        width: 380,
        maxWidth: "calc(100vw - 40px)",
        background:
          "linear-gradient(135deg, rgba(0, 194, 168, 0.10) 0%, rgba(14, 165, 233, 0.05) 100%), rgba(15, 25, 35, 0.96)",
        border: "1px solid rgba(0, 194, 168, 0.40)",
        borderRadius: 14,
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.55), 0 0 32px rgba(0, 194, 168, 0.20)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        overflow: "hidden",
      }}
    >
      {/* Progress line */}
      <div style={{ height: 3, background: "rgba(30, 48, 72, 0.6)" }}>
        <div
          style={{
            height: "100%",
            width: `${progress}%`,
            background: "linear-gradient(90deg, #00C2A8, #0EA5E9)",
            transition: "width 300ms ease",
            boxShadow: "0 0 10px rgba(0, 194, 168, 0.5)",
          }}
        />
      </div>

      <div style={{ padding: 16 }}>
        {/* Head */}
        <div className="flex items-start justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center rounded-md"
              style={{
                width: 24, height: 24,
                background: "rgba(0, 194, 168, 0.14)",
                border: "1px solid rgba(0, 194, 168, 0.35)",
              }}
            >
              <Sparkles size={11} color="#00C2A8" />
            </div>
            <div>
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  color: "#00C2A8",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
                data-testid="tour-step-indicator"
              >
                Demo · Step {tourStep} of {totalSteps}
              </span>
            </div>
          </div>
          <button
            data-testid="tour-close"
            onClick={stopTour}
            className="flex items-center justify-center rounded-md"
            style={{
              width: 22, height: 22,
              background: "rgba(30, 48, 72, 0.5)",
              border: "1px solid var(--border-divider)",
              color: "var(--text-secondary)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <X size={11} />
          </button>
        </div>

        {/* Title */}
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: "-0.01em",
            color: "var(--text-primary)",
            marginBottom: 4,
          }}
          data-testid="tour-title"
        >
          {currentStep.title}
        </div>

        {/* Body */}
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12.5,
            lineHeight: 1.5,
            color: "var(--text-secondary)",
            margin: "0 0 14px",
          }}
        >
          {currentStep.body}
        </p>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <button
            data-testid="tour-prev"
            onClick={prevStep}
            disabled={tourStep === 1}
            className="inline-flex items-center gap-1.5 rounded-lg"
            style={{
              padding: "7px 11px",
              background: "transparent",
              border: "1px solid var(--border-divider)",
              color: tourStep === 1 ? "var(--text-muted)" : "var(--text-secondary)",
              fontFamily: "Inter, sans-serif",
              fontSize: 12,
              fontWeight: 500,
              cursor: tourStep === 1 ? "not-allowed" : "pointer",
              opacity: tourStep === 1 ? 0.5 : 1,
              transition: "all 150ms ease",
            }}
          >
            <ChevronLeft size={12} />
            Previous
          </button>

          <div className="flex-1" />

          {isLast ? (
            <button
              data-testid="tour-book"
              onClick={nextStep}
              className="inline-flex items-center gap-1.5 rounded-lg"
              style={{
                padding: "8px 14px",
                background: "linear-gradient(135deg, #00C2A8 0%, #0EA5E9 100%)",
                color: "#0F1923",
                fontFamily: "Inter, sans-serif",
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: "-0.005em",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 0 18px rgba(0, 194, 168, 0.40)",
              }}
            >
              Book Your Growth Audit
              <ChevronRight size={13} />
            </button>
          ) : (
            <button
              data-testid="tour-next"
              onClick={nextStep}
              className="inline-flex items-center gap-1.5 rounded-lg"
              style={{
                padding: "7px 12px",
                background: "rgba(0, 194, 168, 0.14)",
                border: "1px solid rgba(0, 194, 168, 0.40)",
                color: "#00C2A8",
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 150ms ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0, 194, 168, 0.22)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0, 194, 168, 0.14)")}
            >
              Next
              <ChevronRight size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
